import { HDate, months } from '@hebcal/core';

/**
 * The days Yizkor is said — the memorial prayer in which every departed is
 * remembered at once, whatever their date of passing. Four a year in the
 * Ashkenazi rite: Yom Kippur, Shemini Atzeret, the last day of Pesach and the
 * last day of Shavuot.
 *
 * They are fixed dates on the Hebrew calendar, so they are named here directly
 * rather than looked up as holidays: none of the three months involved moves in
 * a leap year (only Adar does), and a holiday search would have to match a
 * library's spelling by name. Which "last day" it is depends on where the
 * congregation is — outside Israel, Pesach and Shavuot each run a day longer,
 * and Yizkor is said on that added day.
 */
export interface YizkorDay {
  /** The Hebrew month, numbered as hebcal numbers it (Nisan is 1). */
  month: number;
  day: number;
  name: string;
}

const YOM_KIPPUR: YizkorDay = { month: months.TISHREI, day: 10, name: 'Yom Kippur' };
const SHEMINI_ATZERET: YizkorDay = { month: months.TISHREI, day: 22, name: 'Shemini Atzeret' };

/** Outside Israel: the eighth day of Pesach and the second day of Shavuot. */
export const YIZKOR_DAYS_DIASPORA: readonly YizkorDay[] = [
  YOM_KIPPUR,
  SHEMINI_ATZERET,
  { month: months.NISAN, day: 22, name: 'the last day of Pesach' },
  { month: months.SIVAN, day: 7, name: 'the second day of Shavuot' },
];

/** In Israel, where Yom Tov is a single day: the seventh day of Pesach, and Shavuot. */
export const YIZKOR_DAYS_ISRAEL: readonly YizkorDay[] = [
  YOM_KIPPUR,
  SHEMINI_ATZERET,
  { month: months.NISAN, day: 21, name: 'the last day of Pesach' },
  { month: months.SIVAN, day: 6, name: 'Shavuot' },
];

export interface YizkorOptions {
  /** Whether the congregation keeps the Israeli calendar. Off by default. */
  inIsrael?: boolean;
}

export const yizkorDaysFor = (options?: YizkorOptions): readonly YizkorDay[] =>
  options?.inIsrael ? YIZKOR_DAYS_ISRAEL : YIZKOR_DAYS_DIASPORA;

/**
 * Whether the organization keeps the Israeli calendar — the same reading of its
 * country the Zmanim page makes, so the two can never disagree.
 */
export const isIsraelOrganization = (
  organization: Record<string, any> | null | undefined
): boolean => {
  const country = String(organization?.country || '').trim().toLowerCase();
  return country === 'israel' || country === 'il';
};

/**
 * The Yizkor day `date` falls on, or null.
 *
 * `date` is a civil calendar day, read as the Hebrew day that shares its
 * daylight hours — the same reading every other yahrzeit date in the app gets.
 * Yizkor is said in the morning service, so that is the day a screen should
 * carry every yahrzeit through, not the evening before.
 */
export const yizkorDayOn = (date: Date, options?: YizkorOptions): YizkorDay | null => {
  let hebrew: HDate;
  try {
    hebrew = new HDate(date);
  } catch {
    return null;
  }
  const month = hebrew.getMonth();
  const day = hebrew.getDate();
  return yizkorDaysFor(options).find((y) => y.month === month && y.day === day) || null;
};

export const isYizkorDay = (date: Date, options?: YizkorOptions): boolean =>
  yizkorDayOn(date, options) !== null;

/**
 * The next Yizkor day on or after `from` — `from` itself when it is one.
 *
 * This Hebrew year and the next are enough: the four days are spread through
 * the year, so the wait from any day to the next Yizkor is always under a year.
 */
export const nextYizkorDay = (
  from: Date,
  options?: YizkorOptions
): { name: string; date: Date } | null => {
  const start = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  let year: number;
  try {
    year = new HDate(start).getFullYear();
  } catch {
    return null;
  }

  return (
    [year, year + 1]
      .flatMap((hebrewYear) =>
        yizkorDaysFor(options).map((day) => ({
          name: day.name,
          date: new HDate(day.day, day.month, hebrewYear).greg(),
        }))
      )
      .filter((candidate) => candidate.date.getTime() >= start.getTime())
      .sort((a, b) => a.date.getTime() - b.date.getTime())[0] || null
  );
};
